import CheckRounded from '@mui/icons-material/CheckRounded';
import PaletteRounded from '@mui/icons-material/PaletteRounded';
import WarningAmberRounded from '@mui/icons-material/WarningAmberRounded';
import {
  Box,
  ButtonBase,
  InputAdornment,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { fonts } from '../../../design/theme';
import { accentScale, contrastRatio, parseHex, toHex, usableBrand } from '../../../design/color';

export const BRAND_PRESETS = [
  { name: 'PackPilot green', hex: '#05473E' },
  { name: 'Indigo', hex: '#4F46E5' },
  { name: 'Royal blue', hex: '#1565C0' },
  { name: 'Ocean', hex: '#0369A1' },
  { name: 'Teal', hex: '#0F766E' },
  { name: 'Emerald', hex: '#047857' },
  { name: 'Violet', hex: '#7C3AED' },
  { name: 'Orchid', hex: '#A21CAF' },
  { name: 'Crimson', hex: '#BE123C' },
  { name: 'Ember', hex: '#C2410C' },
  { name: 'Graphite', hex: '#334155' },
] as const;

/** True when white text on `hex` meets WCAG AA (so the app will use it as the accent). */
export function isUsableBrand(hex: string): boolean {
  const rgb = parseHex(hex);
  return rgb !== null && contrastRatio(rgb, [255, 255, 255]) >= 4.5;
}

/** Swatches, a native colour picker and a hex field. */
export function ThemeColorField({
  value,
  onChange,
  error,
  disabled,
}: {
  value: string;
  onChange: (hex: string) => void;
  error?: string | undefined;
  disabled?: boolean;
}) {
  const valid = parseHex(value) !== null;
  const usable = valid && isUsableBrand(value);
  const normalized = valid ? value.toUpperCase() : value;
  return (
    <Stack spacing={2}>
      <Box
        role="radiogroup"
        aria-label="Brand colour presets"
        sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.25 }}
      >
        {BRAND_PRESETS.map((p) => {
          const selected = normalized === p.hex;
          return (
            <Tooltip key={p.hex} title={p.name}>
              <ButtonBase
                role="radio"
                aria-checked={selected}
                aria-label={p.name}
                disabled={disabled}
                onClick={() => onChange(p.hex)}
                sx={{
                  width: 36,
                  height: 36,
                  borderRadius: '11px',
                  color: '#fff',
                  background: `linear-gradient(135deg, ${accentScale(usableBrand(p.hex))[400]}, ${p.hex})`,
                  boxShadow: selected
                    ? `0 0 0 2px var(--mui-palette-background-paper), 0 0 0 4px ${p.hex}`
                    : `inset 0 0 0 1px rgba(0,0,0,0.08), 0 2px 6px -2px ${p.hex}`,
                  transition: 'transform 140ms, box-shadow 140ms',
                  '&:hover': { transform: 'translateY(-1px) scale(1.05)' },
                }}
              >
                {selected && <CheckRounded sx={{ fontSize: 18 }} />}
              </ButtonBase>
            </Tooltip>
          );
        })}
      </Box>
      <Stack direction="row" spacing={1.5} sx={{ alignItems: 'flex-start' }}>
        <Box
          component="label"
          sx={{
            position: 'relative',
            width: 44,
            height: 40,
            flexShrink: 0,
            borderRadius: '10px',
            border: 1,
            borderColor: 'divider',
            display: 'grid',
            placeItems: 'center',
            cursor: disabled ? 'default' : 'pointer',
            bgcolor: valid ? value : 'background.paper',
            color: '#fff',
            overflow: 'hidden',
          }}
        >
          <PaletteRounded sx={{ fontSize: 18, filter: 'drop-shadow(0 1px 1px rgba(0,0,0,.4))' }} />
          <Box
            component="input"
            type="color"
            aria-label="Pick a custom colour"
            disabled={disabled}
            value={valid ? toHex(parseHex(value) ?? [0, 0, 0]) : '#05473e'}
            onChange={(e) => onChange(e.target.value.toUpperCase())}
            sx={{ position: 'absolute', inset: 0, opacity: 0, cursor: 'inherit' }}
          />
        </Box>
        <TextField
          label="Hex colour"
          value={value}
          disabled={disabled}
          onChange={(e) => {
            const v = e.target.value.trim();
            onChange(v.startsWith('#') || v === '' ? v : `#${v}`);
          }}
          error={Boolean(error)}
          helperText={error}
          sx={{ maxWidth: 180 }}
          slotProps={{
            htmlInput: { maxLength: 7, spellCheck: false },
            input: {
              sx: { fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace' },
              startAdornment: valid ? undefined : <InputAdornment position="start" />,
            },
          }}
        />
      </Stack>
      {valid && !usable && (
        <Stack
          direction="row"
          spacing={1}
          sx={{ alignItems: 'flex-start', color: 'warning.main', maxWidth: 460 }}
        >
          <WarningAmberRounded sx={{ fontSize: 18, mt: '1px' }} />
          <Typography variant="body2" sx={{ color: 'inherit' }}>
            This colour is too light for white text on buttons, so the app keeps its default accent.
            Pick a deeper shade for it to take effect.
          </Typography>
        </Stack>
      )}
    </Stack>
  );
}

/** A miniature of the app re-tinted with the chosen colour (live preview). */
export function ThemePreview({ hex, shopName }: { hex: string; shopName: string }) {
  const s = accentScale(usableBrand(hex));
  const nav = ['Dashboard', 'Invoices', 'Customers', 'Products'];
  return (
    <Box
      role="img"
      aria-label="Preview of the app in the chosen colour"
      sx={(t) => ({
        borderRadius: '16px',
        border: 1,
        borderColor: 'divider',
        overflow: 'hidden',
        bgcolor: 'background.default',
        boxShadow: '0 12px 24px -10px rgba(16,24,40,0.18)',
        display: 'grid',
        gridTemplateColumns: '112px 1fr',
        minHeight: 236,
        fontSize: 11,
        transition: 'all 200ms',
        ...t.applyStyles('dark', { boxShadow: '0 12px 24px -10px rgba(0,0,0,0.6)' }),
      })}
    >
      <Box sx={{ bgcolor: 'background.paper', borderRight: 1, borderColor: 'divider', p: 1.25 }}>
        <Stack direction="row" spacing={0.75} sx={{ alignItems: 'center', mb: 1.75 }}>
          <Box
            sx={{
              width: 20,
              height: 20,
              borderRadius: '6px',
              flexShrink: 0,
              background: `linear-gradient(135deg, ${s[400]}, ${s[600]} 55%, ${s[700]})`,
            }}
          />
          <Typography noWrap sx={{ fontSize: 11, fontWeight: 700 }}>
            {shopName || 'Your shop'}
          </Typography>
        </Stack>
        {nav.map((n, i) => (
          <Box
            key={n}
            sx={(t) => ({
              px: 1,
              py: 0.6,
              mb: 0.4,
              borderRadius: '7px',
              fontWeight: i === 0 ? 700 : 550,
              color: i === 0 ? s[600] : 'text.secondary',
              bgcolor: i === 0 ? `${s[600]}1A` : 'transparent',
              // Dark mode shows the accent lighter, as the real dark theme does.
              ...(i === 0 ? t.applyStyles('dark', { color: s[300] }) : {}),
            })}
          >
            {n}
          </Box>
        ))}
      </Box>
      <Box sx={{ p: 1.75 }}>
        <Stack direction="row" sx={{ alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography sx={{ fontSize: 13, fontWeight: 700, fontFamily: fonts.display }}>
            Today
          </Typography>
          <Box
            sx={{
              px: 1.25,
              py: 0.5,
              borderRadius: '7px',
              color: '#fff',
              fontWeight: 650,
              bgcolor: s[600],
              backgroundImage: 'linear-gradient(180deg, rgba(255,255,255,.14), transparent)',
              boxShadow: `0 4px 10px -4px ${s[600]}`,
            }}
          >
            New sale
          </Box>
        </Stack>
        <Box
          sx={{
            mt: 1.25,
            p: 1.25,
            borderRadius: '10px',
            bgcolor: 'background.paper',
            border: 1,
            borderColor: 'divider',
          }}
        >
          <Typography sx={{ fontSize: 10, color: 'text.secondary' }}>Sales · 30 days</Typography>
          <Typography sx={{ fontSize: 16, fontWeight: 750 }} className="tabular">
            Rs 248.5K
          </Typography>
          <Box component="svg" viewBox="0 0 200 44" sx={{ width: '100%', height: 44 }}>
            <path
              d="M0 36 C25 34 35 22 60 25 S100 34 120 20 S160 10 200 4 L200 44 L0 44Z"
              fill={s[500]}
              opacity={0.14}
            />
            <path
              d="M0 36 C25 34 35 22 60 25 S100 34 120 20 S160 10 200 4"
              fill="none"
              stroke={s[600]}
              strokeWidth={2.2}
              strokeLinecap="round"
            />
          </Box>
        </Box>
        <Stack direction="row" spacing={0.75} sx={{ mt: 1.25, alignItems: 'center' }}>
          <Box
            sx={{
              px: 1,
              py: 0.25,
              borderRadius: 999,
              fontWeight: 650,
              color: s[700],
              bgcolor: s[50],
              border: `1px solid ${s[200]}`,
            }}
          >
            Paid
          </Box>
          <Box sx={{ flex: 1, height: 5, borderRadius: 999, bgcolor: 'action.hover' }}>
            <Box sx={{ width: '64%', height: '100%', borderRadius: 999, bgcolor: s[500] }} />
          </Box>
        </Stack>
      </Box>
    </Box>
  );
}
